import { type CSSProperties } from "react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

/**
 * The hero intro shown above the catalog when the user has no
 * integrations connected yet.
 *
 * Unlike the Overview empty state (which replaces the whole dashboard),
 * this is a BANNER-STYLE hero. The catalog grid still renders directly
 * below so the user can start connecting immediately. The CTAs live on
 * the individual cards, not here.
 *
 * Automatically hides once the user has at least one integration
 * connected (the page composer renders this component only when
 * getConnected(integrations).length === 0).
 *
 * Mobile responsive: smaller plug icon, tighter padding, smaller type.
 */

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  lineSoft: "#E7EAF0",
};

export function IntegrationEmptyState() {
  const { isMobile } = useBreakpoint();

  const wrapStyle: CSSProperties = {
    padding: isMobile ? "28px 16px 24px" : "40px 24px 32px",
    textAlign: "center",
    borderBottom: `1px solid ${PALETTE.lineSoft}`,
  };

  const iconStyle: CSSProperties = {
    fontSize: isMobile ? 44 : 56,
    lineHeight: 1,
    marginBottom: 14,
  };

  const titleStyle: CSSProperties = {
    fontSize: isMobile ? 19 : 22,
    fontWeight: 700,
    margin: "0 0 8px",
    color: PALETTE.ink,
    letterSpacing: "-0.015em",
  };

  const bodyStyle: CSSProperties = {
    fontSize: isMobile ? 12.5 : 13.5,
    color: PALETTE.ink3,
    maxWidth: 480,
    margin: "0 auto",
    lineHeight: 1.6,
  };

  return (
    <div style={wrapStyle}>
      <div style={iconStyle} aria-hidden="true">
        🔌
      </div>
      <h2 style={titleStyle}>Connect your sales channels</h2>
      <p style={bodyStyle}>
        Pick an integration below. Novala pulls every charge, refund, and
        payout automatically, matches them to the right account, and keeps
        your books up to date.
      </p>
    </div>
  );
}